import {
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { D365Checkbox, D365TextField } from '@/components/D365FormControls';
import { RecordFormLayout } from '@/components/RecordFormLayout';
import {
  createClientContact,
  getClientContacts,
  updateClientContact,
} from '@/features/clients/api/clients';
import type { ClientContactInput, ClientContactSummary } from '@/features/clients/types/Client';
const empty: ClientContactInput = {
  name: '',
  email: '',
  phone: '',
  homePhone: '',
  notifyByEmail: false,
  notifyBySms: false,
};
export function ContactFormPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const p = useParams();
  const clientId = Number(p.clientId);
  const isNew = p.contactId === 'new';
  const id = Number(p.contactId);
  const [v, setV] = useState(empty);
  const [savedValue, setSavedValue] = useState(empty);
  const closeAfterSave = useRef(false);
  const q = useQuery({
    queryKey: ['clients', clientId, 'contacts'],
    queryFn: () => getClientContacts(clientId),
    enabled: !isNew,
  });
  const item = q.data?.find((x) => x.id === id);
  useEffect(() => {
    // The API result is the initial value for this routed editor.
    if (item) {
      const loaded = {
        ...item,
        email: item.email ?? '',
        phone: item.phone ?? '',
        homePhone: item.homePhone ?? '',
      };
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setV(loaded);
      setSavedValue(loaded);
    }
  }, [item]);
  const m = useMutation({
    mutationFn: () =>
      isNew ? createClientContact(clientId, v) : updateClientContact(clientId, id, v),
    onSuccess: async (savedContact) => {
      if (isNew && !closeAfterSave.current) {
        qc.setQueryData<ClientContactSummary[]>(
          ['clients', clientId, 'contacts'],
          (current = []) => [
            ...current.filter((contact) => contact.id !== savedContact.id),
            {
              id: savedContact.id,
              name: savedContact.name,
              email: savedContact.email ?? null,
              phone: savedContact.phone ?? null,
              homePhone: savedContact.home_phone ?? null,
              notifyByEmail: Boolean(savedContact.notify_by_email),
              notifyBySms: Boolean(savedContact.notify_by_sms),
            },
          ],
        );
      }
      await qc.invalidateQueries({ queryKey: ['clients', clientId, 'contacts'] });
      setSavedValue(v);
      if (closeAfterSave.current) void nav(-1);
      else if (isNew) void nav(`/clients/${clientId}/contacts/${savedContact.id}`, { replace: true });
    },
  });
  const set = <K extends keyof ClientContactInput>(k: K, value: ClientContactInput[K]) =>
    setV((x) => ({ ...x, [k]: value }));
  if (!isNew && (q.isPending || q.isLoading))
    return (
      <Stack alignItems="center" sx={{ p: 8 }}>
        <CircularProgress />
      </Stack>
    );
  if (!isNew && !item)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This contact could not be loaded.
      </Alert>
    );
  return (
    <RecordFormLayout
      title={isNew ? 'New Contact' : v.name}
      type="Contact"
      saving={m.isPending}
      disabled={!v.name.trim()}
      dirty={JSON.stringify(v) !== JSON.stringify(savedValue)}
      error={m.isError}
      onBack={() => void nav(`/clients/${clientId}`)}
      onSave={() => {
        closeAfterSave.current = false;
        m.mutate();
      }}
      onSaveAndClose={() => {
        closeAfterSave.current = true;
        m.mutate();
      }}
    >
      <Stack spacing={1}>
        <D365TextField required label="Name" value={v.name} onChange={(e) => set('name', e.target.value)} />
        <D365TextField label="Email" type="email" value={v.email} onChange={(e) => set('email', e.target.value)} />
        <D365TextField label="Mobile" value={v.phone} onChange={(e) => set('phone', e.target.value)} />
        <D365TextField label="Other" value={v.homePhone} onChange={(e) => set('homePhone', e.target.value)} />
        <D365Checkbox label="Notify by email" checked={v.notifyByEmail} onChange={(e) => set('notifyByEmail', e.target.checked)} />
        <D365Checkbox label="Notify by SMS" checked={v.notifyBySms} onChange={(e) => set('notifyBySms', e.target.checked)} />
      </Stack>    </RecordFormLayout>
  );
}


